import { FC, ReactElement, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Icons } from '~/assets/fonts/Icons/icons';
import { BUTTON_TITLE } from '~/constants/main';
import theme from '~/constants/styles';
import { localeStore } from '~/mobx/localesStore';
import { confirmDelete } from '~/utils/modalConfirmations';
import Icon from '~/view/components/Controls/Icon/Icon';
import ModalPopup from '~/view/components/ModalPopup/ModalPopup';
import { TProfile } from '~/view/containers/ProfilesList';
import './style.scss';

interface IProps {
  selectedProfile?: TProfile;
  onDeleteProfile?: (profileId: TProfile['id']) => void;
  onDuplicateProfile?: (profileId: TProfile['id']) => void;
  logPath?: string;
  notEditable?: boolean;
}

export const ProfileBranchesDots: FC<IProps> = ({ selectedProfile, ...props }: IProps): ReactElement | null => {
  const modalRef = useRef<ModalPopup | null>(null);

  const isDefaultProfile = useMemo((): boolean => Boolean(selectedProfile?.is_default), [selectedProfile]);

  const logPath = useMemo((): string | null => {
    if (!selectedProfile || !props.logPath) return null;
    return props.logPath + '/' + String(selectedProfile.id) + '/logs';
  }, [selectedProfile, props.logPath]);

  const changeListVisibility = useCallback(() => {
    modalRef.current?.openModalPopup();
  }, [modalRef.current]);

  const onDeleteProfile = useCallback(() => {
    if (!selectedProfile) return;

    confirmDelete({ message: localeStore.t('confirm_profile_deletion'), onConfirm: () => props.onDeleteProfile?.(selectedProfile.id) });
  }, [selectedProfile]);

  const handleProfileDuplicate = () => {
    if (selectedProfile) props.onDuplicateProfile?.(selectedProfile.id);
  };

  if (!selectedProfile || (isDefaultProfile && !logPath)) return null;

  return (
    <ModalPopup
      autoClose
      horizontal="end"
      ref={modalRef}
      content={
        <div className="profile-branches-dots animate__faster animate__animated animate__fadeInLeft">
          {props.notEditable || isDefaultProfile ? null : (
            <button onClick={onDeleteProfile} className="profile-branches-dots__btn-action">
              <p className="profile-branches-dots__btn-action-title">{localeStore.t(BUTTON_TITLE.DELETE)}</p>
            </button>
          )}

          {typeof props.onDuplicateProfile === 'function' ? (
            <button onClick={handleProfileDuplicate} className="profile-branches-dots__btn-action">
              <p className="profile-branches-dots__btn-action-title">{localeStore.t('txt_duplicate')}</p>
            </button>
          ) : null}

          {logPath ? (
            <Link to={logPath} className="profile-branches-dots__btn-action">
              <p className="profile-branches-dots__btn-action-title color-mainblue">{localeStore.t('view_log')}</p>
            </Link>
          ) : null}
        </div>
      }
    >
      <button onClick={changeListVisibility} disabled={!selectedProfile} className="profile-branches-dots__btn-dots">
        <Icon name={Icons.MenuDots} size={18} color={theme.colors.dark} />
      </button>
    </ModalPopup>
  );
};
